import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, BarChart3, Users, Infinity as InfinityIcon } from "lucide-react";
import ChartVisual from "@/components/ChartVisual";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const cardRef = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 60, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 60, damping: 18 });

  const { scrollY } = useScroll();
  const glowY = useTransform(scrollY, [0, 600], [0, 120]);
  const chartY = useTransform(scrollY, [0, 600], [0, -60]);

  const onMove = (e) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width);
    my.set((e.clientY - rect.top) / rect.height);
  };

  return (
    <section id="top" className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-28 pb-16" data-testid="hero-section">
      <motion.div style={{ y: glowY }} className="absolute -top-40 right-[-15%] w-[700px] h-[700px] rounded-full bg-gold/10 blur-[140px] pointer-events-none" />
      <div className="absolute inset-0 opacity-[0.35] pointer-events-none"
        style={{ backgroundImage: "linear-gradient(#1E293B 1px, transparent 1px), linear-gradient(90deg, #1E293B 1px, transparent 1px)", backgroundSize: "72px 72px", maskImage: "radial-gradient(ellipse 90% 70% at 50% 30%, black 30%, transparent 75%)" }} />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, scale: 0.55, filter: "blur(10px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ duration: 1, delay: 0.1, ease: EASE }}
            className="relative inline-block mb-7"
            data-testid="hero-logo-reveal"
          >
            <motion.span
              className="absolute inset-0 rounded-full bg-gold/25 blur-xl"
              animate={{ opacity: [0.35, 0.7, 0.35], scale: [1, 1.15, 1] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
            <img src="/atlas-logo-256.png" alt="Atlas Trading Community logo" className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full ring-1 ring-gold/40 object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
            className="inline-flex items-center gap-3 border border-gold/25 rounded-full px-4 py-1.5 mb-8"
            data-testid="hero-overline-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
            <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-gold/90">
              Analysis · Technology · Independence
            </span>
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-[5.4rem] text-slate-50" data-testid="hero-title">
            <MaskedLine delay={0.75}>It's a system,</MaskedLine>
            <MaskedLine delay={0.9} className="text-gold-gradient">not signals.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15, ease: EASE }}
            className="mt-7 max-w-xl text-sm sm:text-base text-slate-400 leading-relaxed"
            data-testid="hero-subtitle"
          >
            Atlas gives you the tools, the zones and the analysis — you make the decisions.
            A custom-coded indicator, real market education and a private community built to
            make Gold &amp; Forex traders genuinely independent.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-how-it-works"
              onClick={() => scrollToSection("how-it-works")}
              className="group inline-flex items-center gap-2.5 bg-gold text-ink font-semibold text-sm px-7 py-3.5 rounded-sm hover:bg-gold-bright transition-colors duration-300"
            >
              See How It Works
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-join"
              onClick={() => scrollToSection("join")}
              className="inline-flex items-center gap-2.5 border border-slate-600 text-slate-200 font-medium text-sm px-7 py-3.5 rounded-sm hover:border-gold hover:text-gold transition-colors duration-300"
            >
              Join Atlas
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.55 }}
            className="mt-10 sm:mt-14 grid grid-cols-3 max-w-lg gap-3 sm:gap-6 border-t border-line/70 pt-6 sm:pt-7"
            data-testid="hero-metrics-strip"
          >
            {[
              { icon: BarChart3, top: "Custom", bottom: "Coded Indicator" },
              { icon: Users, top: "Private", bottom: "Member Community" },
              { icon: InfinityIcon, top: "One-Time", bottom: "Lifetime Access" },
            ].map(({ icon: Icon, top, bottom }, i) => (
              <div key={i} className="flex items-start gap-3">
                <Icon size={17} className="text-gold mt-0.5 shrink-0" />
                <div>
                  <p className="font-display font-bold text-slate-100 text-sm sm:text-base leading-tight">{top}</p>
                  <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5">{bottom}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative" style={{ perspective: 1200 }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 1.0, ease: EASE }}
            style={{ y: chartY }}
          >
            <motion.div
              ref={cardRef}
              onMouseMove={onMove}
              onMouseLeave={() => { mx.set(0.5); my.set(0.5); }}
              style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
              className="relative glass-panel gold-ring chart-frame border border-line p-4 sm:p-5"
              data-testid="hero-chart-card"
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-gold" />
                  <span className="font-mono text-[11px] tracking-[0.25em] text-slate-300">XAUUSD · 1H</span>
                </div>
                <span className="font-mono text-[10px] tracking-widest text-slate-500">ATLAS INDICATOR</span>
              </div>
              <ChartVisual seed={11} layers={{ zones: true, ribbon: true, sweeps: true }} className="w-full h-auto" />
              <div className="absolute bottom-6 left-6 glass-panel border border-gold/30 rounded-sm px-3 py-2" style={{ transform: "translateZ(40px)" }}>
                <p className="font-mono text-[10px] tracking-[0.2em] text-gold">BUY ZONE RESPECTED</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Structure held. Plan executed.</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
